export const DEFAULT_TIME_ZONE = 'Asia/Dushanbe';

type TimeZoneOption = {
    label: string;
    value: string;
};

type IntlWithSupportedValues = typeof Intl & {
    supportedValuesOf?: (key: 'timeZone') => string[];
};

export const getBrowserTimeZone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIME_ZONE;
    } catch {
        return DEFAULT_TIME_ZONE;
    }
};

const PRIORITY_TIME_ZONE_LABELS: Record<string, string> = {
    'Asia/Dushanbe': 'Душанбе (Asia/Dushanbe)',
    'Asia/Tashkent': 'Ташкент (Asia/Tashkent)',
    'Asia/Samarkand': 'Самарканд (Asia/Samarkand)',
    'Asia/Almaty': 'Алматы (Asia/Almaty)',
    'Asia/Bishkek': 'Бишкек (Asia/Bishkek)',
    'Europe/Moscow': 'Москва (Europe/Moscow)',
    UTC: 'UTC',
};

const PRIORITY_TIME_ZONES = Object.keys(PRIORITY_TIME_ZONE_LABELS);

const FALLBACK_TIME_ZONES = [
    'UTC',
    'Africa/Abidjan',
    'Africa/Accra',
    'Africa/Addis_Ababa',
    'Africa/Algiers',
    'Africa/Cairo',
    'Africa/Casablanca',
    'Africa/Johannesburg',
    'Africa/Lagos',
    'Africa/Nairobi',
    'America/Anchorage',
    'America/Argentina/Buenos_Aires',
    'America/Bogota',
    'America/Caracas',
    'America/Chicago',
    'America/Denver',
    'America/Guatemala',
    'America/Halifax',
    'America/Los_Angeles',
    'America/Mexico_City',
    'America/New_York',
    'America/Phoenix',
    'America/Santiago',
    'America/Sao_Paulo',
    'America/Toronto',
    'America/Vancouver',
    'Asia/Almaty',
    'Asia/Amman',
    'Asia/Aqtau',
    'Asia/Aqtobe',
    'Asia/Ashgabat',
    'Asia/Baghdad',
    'Asia/Baku',
    'Asia/Bangkok',
    'Asia/Beirut',
    'Asia/Bishkek',
    'Asia/Colombo',
    'Asia/Dhaka',
    'Asia/Dubai',
    'Asia/Dushanbe',
    'Asia/Hong_Kong',
    'Asia/Ho_Chi_Minh',
    'Asia/Jakarta',
    'Asia/Jerusalem',
    'Asia/Kabul',
    'Asia/Karachi',
    'Asia/Kathmandu',
    'Asia/Kolkata',
    'Asia/Kuwait',
    'Asia/Macau',
    'Asia/Muscat',
    'Asia/Novosibirsk',
    'Asia/Omsk',
    'Asia/Qatar',
    'Asia/Riyadh',
    'Asia/Samarkand',
    'Asia/Seoul',
    'Asia/Shanghai',
    'Asia/Singapore',
    'Asia/Tashkent',
    'Asia/Tbilisi',
    'Asia/Tehran',
    'Asia/Tokyo',
    'Asia/Ulaanbaatar',
    'Asia/Yekaterinburg',
    'Atlantic/Reykjavik',
    'Australia/Adelaide',
    'Australia/Brisbane',
    'Australia/Darwin',
    'Australia/Melbourne',
    'Australia/Perth',
    'Australia/Sydney',
    'Europe/Amsterdam',
    'Europe/Athens',
    'Europe/Berlin',
    'Europe/Brussels',
    'Europe/Bucharest',
    'Europe/Istanbul',
    'Europe/Kiev',
    'Europe/Lisbon',
    'Europe/London',
    'Europe/Madrid',
    'Europe/Minsk',
    'Europe/Moscow',
    'Europe/Paris',
    'Europe/Prague',
    'Europe/Rome',
    'Europe/Samara',
    'Europe/Stockholm',
    'Europe/Vienna',
    'Europe/Warsaw',
    'Europe/Zurich',
    'Indian/Maldives',
    'Pacific/Auckland',
    'Pacific/Fiji',
    'Pacific/Guam',
    'Pacific/Honolulu',
    'Pacific/Tongatapu',
];

const twoDigits = (value: number) => value < 10 ? `0${value}` : `${value}`;

const getTimeZoneOffsetMinutes = (timeZone: string, date = new Date()) => {
    try {
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        });
        const parts = formatter.formatToParts(date);
        const getPart = (type: string) => Number(parts.find((part) => part.type === type)?.value || 0);
        const timeZoneDate = Date.UTC(
            getPart('year'),
            getPart('month') - 1,
            getPart('day'),
            getPart('hour') % 24,
            getPart('minute'),
            getPart('second'),
        );
        const utcDate = Date.UTC(
            date.getUTCFullYear(),
            date.getUTCMonth(),
            date.getUTCDate(),
            date.getUTCHours(),
            date.getUTCMinutes(),
            date.getUTCSeconds(),
        );

        return Math.round((timeZoneDate - utcDate) / 60000);
    } catch {
        return null;
    }
};

const formatOffsetValue = (prefix: 'UTC' | 'GMT', offsetMinutes: number) => {
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const absoluteMinutes = Math.abs(offsetMinutes);
    const hours = Math.floor(absoluteMinutes / 60);
    const minutes = absoluteMinutes % 60;

    return minutes > 0
        ? `${prefix}${sign}${hours}:${twoDigits(minutes)}`
        : `${prefix}${sign}${hours}`;
};

const formatTimeZoneOffsetLabel = (timeZone: string) => {
    const offsetMinutes = getTimeZoneOffsetMinutes(timeZone);

    if (offsetMinutes === null) {
        return '';
    }

    return `(${formatOffsetValue('UTC', offsetMinutes)} / ${formatOffsetValue('GMT', offsetMinutes)})`;
};

const formatTimeZoneLabel = (timeZone: string) => {
    const offsetLabel = formatTimeZoneOffsetLabel(timeZone);
    const prefix = offsetLabel ? `${offsetLabel} ` : '';

    if (PRIORITY_TIME_ZONE_LABELS[timeZone]) {
        return `${prefix}${PRIORITY_TIME_ZONE_LABELS[timeZone]}`;
    }

    const city = timeZone.split('/').pop()?.replace(/_/g, ' ') || timeZone;
    return `${prefix}${city} (${timeZone})`;
};

const getSupportedTimeZones = () => {
    const intlWithSupportedValues = Intl as IntlWithSupportedValues;

    try {
        if (typeof intlWithSupportedValues.supportedValuesOf === 'function') {
            return intlWithSupportedValues.supportedValuesOf('timeZone');
        }
    } catch {
        return FALLBACK_TIME_ZONES;
    }

    return FALLBACK_TIME_ZONES;
};

export const BASE_TIME_ZONE_OPTIONS: TimeZoneOption[] = [
    ...PRIORITY_TIME_ZONES.map((timeZone) => ({
        label: formatTimeZoneLabel(timeZone),
        value: timeZone,
    })),
    ...getSupportedTimeZones()
        .filter((timeZone) => !PRIORITY_TIME_ZONES.includes(timeZone))
        .sort((a, b) => a.localeCompare(b))
        .map((timeZone) => ({
            label: formatTimeZoneLabel(timeZone),
            value: timeZone,
        })),
];

export const getTimeZoneOptions = (...values: Array<string | undefined | null>) => {
    const options = [...BASE_TIME_ZONE_OPTIONS];

    values.filter(Boolean).forEach((value) => {
        const timeZone = value as string;
        if (!options.some((option) => option.value === timeZone)) {
            options.unshift({label: formatTimeZoneLabel(timeZone), value: timeZone});
        }
    });

    return options;
};
